import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FileText, Trash2, Copy } from 'lucide-react';
import { getSelectedPdfText } from '@/utils/pdfSelection';

interface UserDefinedField {
  id: string;
  name: string;
  value: string;
}

interface UserDefinedFieldsPanelProps {
  fields: UserDefinedField[];
  onChange: (fields: UserDefinedField[]) => void;
  onSave?: (fields: UserDefinedField[]) => void;
  pdfSelectionProvider?: () => Promise<string>;
}

export function UserDefinedFieldsPanel({
  fields,
  onChange,
  onSave,
  pdfSelectionProvider = getSelectedPdfText
}: UserDefinedFieldsPanelProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [lastSelectedText, setLastSelectedText] = useState<string>('');

  // Setup PDF text selection
  useEffect(() => {
    const cleanup = setupPdfTextSelection((text) => {
      setLastSelectedText(text);
    });

    return cleanup;
  }, []);

  const addRow = () => {
    const newField: UserDefinedField = {
      id: `udf_${Date.now()}`,
      name: '',
      value: ''
    };
    onChange([...(fields || []), newField]);
    setActiveIndex((fields || []).length);
  };

  const updateField = (index: number, patch: Partial<{ name: string; value: string }>) => {
    const updatedFields = (fields || []).map((field, idx) => 
      idx === index ? { ...field, ...patch } : field
    );
    onChange(updatedFields);
  };

  const deleteField = (index: number) => {
    const updatedFields = (fields || []).filter((_, idx) => idx !== index);
    onChange(updatedFields);
    if (activeIndex === index) {
      setActiveIndex(null);
    }
  };

  const pasteFromPdf = async (index: number) => {
    try {
      const text = await pdfSelectionProvider();
      if (text) {
        updateField(index, { value: text });
      }
    } catch (error) {
      console.error('Failed to get PDF text:', error);
    }
  };

  const pasteLastSelection = (index: number) => {
    if (lastSelectedText) {
      updateField(index, { value: lastSelectedText });
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(fields || []);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            User-Defined Fields
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastSelectedText && (
              <Badge variant="outline" className="text-xs">
                Text selected: "{lastSelectedText.substring(0, 20)}..."
              </Badge>
            )}
            <Button size="sm" onClick={addRow} className="gap-1">
              <FileText className="h-4 w-4" />
              Add Field
            </Button>
            {onSave && (
              <Button size="sm" variant="outline" onClick={handleSave}>
                Save
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 font-medium text-sm text-gray-600 sticky top-0 bg-white">
                  Field Name
                </th>
                <th className="text-left py-2 px-3 font-medium text-sm text-gray-600 sticky top-0 bg-white">
                  Value
                </th>
                <th className="text-center py-2 px-3 font-medium text-sm text-gray-600 sticky top-0 bg-white w-24">
                  PDF Paste
                </th>
                <th className="text-center py-2 px-3 font-medium text-sm text-gray-600 sticky top-0 bg-white w-16">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {(fields || []).map((field, index) => (
                <tr key={field.id} className={`border-b hover:bg-gray-50 ${index % 2 === 1 ? 'bg-muted/30' : ''}`}>
                  <td className="py-2 px-3">
                    <Input
                      className="w-full"
                      value={field.name}
                      onChange={(e) => updateField(index, { name: e.target.value })}
                      placeholder="e.g., PO Number, Reference ID"
                      onFocus={() => setActiveIndex(index)}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      className="w-full"
                      value={field.value}
                      onChange={(e) => updateField(index, { value: e.target.value })}
                      onFocus={() => setActiveIndex(index)}
                      placeholder="Value (double-click PDF to paste)"
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <div className="flex gap-1 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => pasteFromPdf(index)}
                        className="h-8 px-2"
                        title="Paste from PDF selection"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      {lastSelectedText && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => pasteLastSelection(index)}
                          className="h-8 px-2"
                          title="Paste last selection"
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteField(index)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {(!fields || fields.length === 0) && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    No user-defined fields added yet. Click "Add Field" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper function to setup PDF text selection
function setupPdfTextSelection(onTextSelected: (text: string) => void): () => void {
  const handleSelection = () => {
    const text = window.getSelection()?.toString().trim();
    if (text) {
      onTextSelected(text);
    }
  };

  document.addEventListener('mouseup', handleSelection);
  document.addEventListener('keyup', handleSelection);

  return () => {
    document.removeEventListener('mouseup', handleSelection);
    document.removeEventListener('keyup', handleSelection);
  };
}
